import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Percent } from "lucide-react";
import { invalidatePlatformFeeCache } from "@/hooks/usePlatformFee";

export default function AdminFeeSettings() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [pctInput, setPctInput] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/fee");
      setCurrent(data.fee_pct);
      setPctInput(data.fee_pct_display.toString());
      setHistory(data.history || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load fee settings.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    const pct = parseFloat(pctInput);
    if (isNaN(pct) || pct < 0 || pct > 10) {
      toast.error("Fee must be between 0 and 10 (%).");
      return;
    }
    if (reason.trim().length < 5) {
      toast.error("Please write a short reason for this fee change.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/settings/fee", {
        fee_pct: pct / 100,
        reason: reason.trim(),
      });
      toast.success(`Platform fee set to ${pct}%.`);
      invalidatePlatformFeeCache();
      setReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save fee.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-fee-settings">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Platform fee</h1>
        <p className="text-sm text-slate-500 mt-1">
          Single source of truth for the platform fee percentage. Applies to the FX widget, withdrawal
          confirmation, and eFIRA generation — everywhere the fee is calculated or displayed.
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Current fee</p>
          <p className="text-3xl font-bold text-slate-900 sf-mono" data-testid="admin-fee-current">
            {current === null ? "—" : `${(current * 100).toFixed(3)}%`}
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">New fee %</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  max="10"
                  value={pctInput}
                  onChange={(e) => setPctInput(e.target.value)}
                  className="h-10 pr-10 sf-mono"
                  data-testid="admin-fee-input"
                />
                <Percent className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Enter a percentage (e.g. 0.5 for 0.5%). Range: 0 – 10%.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
                Reason for change
              </Label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rolled out promotional 0.35% fee for Kerala spice exporters — approved by leadership on 2026-02-16."
                className="w-full min-h-20 mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="admin-fee-reason"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={submit} disabled={saving} data-testid="admin-fee-submit">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  <>Update fee</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Change history ({history.length})
          </p>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No changes recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="border-l-2 border-slate-200 pl-3" data-testid={`admin-fee-history-${h.id}`}>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{new Date(h.timestamp).toLocaleString("en-IN")}</span>
                    <span className="font-medium">{h.admin_email}</span>
                  </div>
                  <p className="text-sm mt-0.5 text-slate-800">
                    <code className="text-xs bg-slate-100 px-1 rounded sf-mono">
                      {(h.old_value * 100).toFixed(3)}%
                    </code>{" "}
                    →{" "}
                    <code className="text-xs bg-slate-100 px-1 rounded sf-mono">
                      {(h.new_value * 100).toFixed(3)}%
                    </code>
                  </p>
                  {h.reason && <p className="text-xs text-slate-600 italic mt-1">{`"${h.reason}"`}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
